import styled from "styled-components";
import Box from "../../../components/Box";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  fetchOrgRevenueStats,
  fetchOrgSubsCount,
} from "../../Owner/ownerAction";
import Graph from "./Graph";
import FundsGraph from "./FundsGraph";

const LEGENDS_INCOME_ADMIN = [
  "Amount recieved online",
  "Amount pending",
  "Amount recieved offline",
];
const LEGENDS_INCOME_OWNER = ["Amount recieved", "Amount pending"];

const TotalRevenueChart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dateRange, setDateRange] = useState(
    new Date().getFullYear().toString()
  );
  const [details, setDetails] = useState({});
  const [pending_details, setPendingDetails] = useState({});
  const [offlineDetails, setOfflineDetails] = useState({});
  const [legend, setLegend] = useState([]);
  const userType = useSelector((state) => state?.appReducer?.userType);
  const memberRevenueStats = useSelector(
    (state) => state.adminReducer.memberRevenueStats
  );
  const orgRevenueStats = useSelector(
    (state) => state.ownerReducer.orgRevenueStats
  );
  const orgSubscriptionCount = useSelector(
    (state) => state.ownerReducer.orgSubscriptionCount
  );
  const transformedResult = Object.entries(details).map(([year, data]) => ({
    name: year.toString(),
    data: data,
  }));

  const getMaxMonth = () => {
    const currentDate = moment();

    if (currentDate.date() === 1) {
      return currentDate.subtract(1, "month").endOf("month");
    }
    return currentDate.endOf("month");
  };

  const pendingTransformedResult = Object.entries(pending_details).map(
    ([year, data]) => ({
      name: year.toString(),
      data: data,
    })
  );
  const offlineTransformedResult = Object.entries(offlineDetails).map(
    ([year, data]) => ({
      name: year.toString(),
      data: data,
    })
  );

  const handleDateRange = (selection) => {
    setDateRange(selection);
    handleRevenueByYear(selection);
  };

//   Get Graph data
  const getData = () => {
    if (userType === "admin" || userType === "viewer") {
      let dataSet = [
        transformedResult ? transformedResult[0] : [],
        pendingTransformedResult ? pendingTransformedResult[0] : [],
        offlineTransformedResult ? offlineTransformedResult[0] : [],
      ];
      return dataSet; // return the transformed data
    }
    if (userType === "product_owner") {
      let amount_data = Array(12).fill(0);
      let pending_amount = Array(12).fill(0);
      if (orgRevenueStats?.length > 0) {
        amount_data = amount_data.map((item, index) => {
          let amount = 0;
          orgRevenueStats?.forEach((element) => {
            if (element.month === index + 1) {
              amount = element.amount;
            }
          });
          return amount;
        });
        pending_amount = pending_amount.map((item, index) => {
          let count = 0;
          orgRevenueStats?.forEach((element) => {
            if (element.month === index + 1) {
              count = element.total_pending_amount;
            }
          });
          return count;
        });
      }
      let series = [
        {
          name: "Amount",
          data: amount_data,
        },
        {
          name: "Pending Amount",
          data: pending_amount,
        },
      ];
      return series;
    }
  };
  const handleRevenueByYear = (year) => {
    // const start_date = moment().year(year).startOf('year').subtract(1, 'years').toISOString();
    const start_date = moment().year(year).startOf("year").toISOString();
    const end_date = moment().year(year).endOf("year").toISOString();
    dispatch(fetchOrgRevenueStats(start_date, end_date));
  };

  useEffect(() => {
    if (userType === "admin" || userType === "viewer") {
      setLegend(LEGENDS_INCOME_ADMIN);
    }
    if (userType === "product_owner") {
      handleRevenueByYear(dateRange);
      const startDate = moment().startOf("year").toISOString();
      const endDate = moment().endOf("year").toISOString();
      dispatch(fetchOrgSubsCount(startDate, endDate));
      setLegend(LEGENDS_INCOME_OWNER);
    }
  }, []);

// Owner Revenue Chart 
  useEffect(() => {
    if (orgRevenueStats?.length > 0 && userType === "product_owner") {
      const result = {};
      orgRevenueStats.forEach((data) => {
        const { year, month } = data;
        const { amount } = data;
        if (!result[year]) {
          result[year] = Array(12).fill(0); // Initialize the array with zeros for all months
          setDetails(result);
        }
        result[year][month - 1] = amount; // Update the amount for the corresponding month
        setDetails(result);
      });
    }
  }, [orgRevenueStats]);

//   Admin Revenue Chart
  useEffect(() => {
    if (memberRevenueStats?.length > 0 && (userType === "admin" || userType === "viewer")) {
      const result = {};
      const pending_result = {};
      const offline_result = {};
      memberRevenueStats.forEach((data) => {
        const { year, month } = data;
        const { amount, total_pending_amount, offlineAmount } = data;
        if (!result[year]) {
          result[year] = Array(12).fill(0); // Initialize the array with zeros for all months
          pending_result[year] = Array(12).fill(0); // Initialize the array with zeros for all months
          offline_result[year] = Array(12).fill(0);
          setDetails(result);
          setPendingDetails(pending_result);
          setOfflineDetails(offline_result);
        }
        result[year][month - 1] = amount; // Update the amount for the corresponding month
        pending_result[year][month - 1] = total_pending_amount; // Update the amount for the corresponding month
        offline_result[year][month - 1] = offlineAmount;
        setDetails(result);
        setPendingDetails(pending_result);
        setOfflineDetails(offline_result);
      });
    } else {
      const result = {};
      const pending_result = {};
      const offline_result = {};
      result[moment().year()] = Array(12).fill(0); // Initialize the array with zeros for all months
      pending_result[moment().year()] = Array(12).fill(0); // Initialize the array with zeros for all months
      offline_result[moment().year()] = Array(12).fill(0);
      setDetails(result);
      setPendingDetails(pending_result);
      setOfflineDetails(offline_result);
    }
  }, [memberRevenueStats]);

  return (
    <>
      <Graph
        dateRange={dateRange}
        dataSet={getData()}
        colors={
          userType === "product_owner"
            ? ["#80CBC4", "#D0004B"]
            : ["#80CBC4", "#D0004B", "#35AFEA"]
        }
        title={"Total Income"}
        handleDateRange={handleDateRange}
        legends={legend}
      />
     {( userType === 'admin' || userType === 'owner') && <FundsGraph
       dateRange={dateRange}
        colors={
          userType === "product_owner"
            ? ["#80CBC4", "#D0004B"]
            : ["#80CBC4", "#D0004B", "#35AFEA"]
        }
        handleDateRange={handleDateRange}
      />}
    </>
  );
};

export default TotalRevenueChart;
